import React, { Suspense, lazy } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import theme from "./theme";
import Header from "./Section/Header";
import Blank from "./Pages/Layout/Blank";
import PageNotFound from "./Pages/PageNotFound";
import Menulevel1 from "./Pages/Layout/Menulevel1";
import Login from "./Pages/UserLogin";
import { UserProvider } from "./UserContext";
import { SnackbarProvider } from "./SnackbarContext";

/**
 * Loads a page only when it is first opened, so its code (and libraries such
 * as the Excel reader and PDF builder) is not part of the initial download.
 * If the page file is missing because a new version was deployed while the
 * app was open, the browser reloads once to get the new files.
 */
const lazyPage = (importPage) =>
  lazy(() =>
    importPage()
      .then((page) => {
        sessionStorage.removeItem("pageReloadedForUpdate");
        return page;
      })
      .catch((error) => {
        if (!sessionStorage.getItem("pageReloadedForUpdate")) {
          sessionStorage.setItem("pageReloadedForUpdate", "1");
          window.location.reload();
          return new Promise(() => {}); // wait for the reload
        }
        throw error;
      })
  );

const LabData = lazyPage(() => import("./Pages/DataFeeding/LabDataFeeding"));
const Chemical = lazyPage(() =>
  import("./Pages/DataFeeding/ChemicalCommericalDataFeeding")
);
const GoDownDomestic = lazyPage(() =>
  import("./Pages/DataFeeding/GoDownDomesticDataFeeding")
);
const GoDownExportImport = lazyPage(() =>
  import("./Pages/DataFeeding/GoDownExportData")
);
const GoDownStock = lazyPage(() => import("./Pages/DataFeeding/GoDownStockData"));
const CaneOffice = lazyPage(() =>
  import("./Pages/DataFeeding/CaneOfficeDataFeeding")
);
const CaneDuesData = lazyPage(() => import("./Pages/DataFeeding/CaneDuesData"));
const ShowJon = lazyPage(() => import("./Pages/DataFeeding/ShowJon"));
const PdfReport = lazyPage(() => import("./Pages/DataFeeding/PdfReport"));
const SeasonData = lazyPage(() => import("./Pages/DataFeeding/SeasonData"));
const UserControl = lazyPage(() => import("./Pages/DataFeeding/UserControl"));
const MenuRight = lazyPage(() => import("./Pages/DataFeeding/MenuRight"));
const PasswordChange = lazyPage(() =>
  import("./Pages/DataFeeding/PasswordChange")
);
const MillDetails = lazyPage(() => import("./Pages/DataFeeding/MillDetails"));
const CreateClient = lazyPage(() => import("./Pages/DataFeeding/CreateClient"));

// Shown while a page's code is downloading (usually a fraction of a second).
const pageLoading = (
  <div className="p-4 text-muted d-flex align-items-center gap-2">
    <span className="spinner-border spinner-border-sm" aria-hidden="true" />
    Loading…
  </div>
);

function App() {
  const location = useLocation();
  // The login and client-setup pages are shown without the header and menu.
  const fullScreenRoutes = ["/login", "/sisl"];
  const isFullScreen = fullScreenRoutes.includes(location.pathname);

  return (
    <ThemeProvider theme={theme}>
      <SnackbarProvider>
        <UserProvider>
          {!isFullScreen && <Header />}

          {!isFullScreen ? (
            <main id="main" className="main">
              <Suspense fallback={pageLoading}>
                <Routes>
                  <Route path="/" element={<Blank />} />
                  <Route path="/:id1" element={<Menulevel1 />} />

                  {/* Each data page serves three routes; the page reads the
                      mode (feeding / Excel upload / approval) from the URL. */}
                  <Route path="/DATA FEEDING/LAB DATA" element={<LabData />} />
                  <Route path="/DATA FEEDING/Chemical" element={<Chemical />} />
                  <Route path="/DATA FEEDING/GoDown Domestic" element={<GoDownDomestic />} />
                  <Route path="/DATA FEEDING/GoDown Export Import" element={<GoDownExportImport />} />
                  <Route path="/DATA FEEDING/GoDown Stock" element={<GoDownStock />} />
                  <Route path="/DATA FEEDING/Cane Office" element={<CaneOffice />} />

                  <Route path="/UPLOAD EXCEL/LAB DATA" element={<LabData />} />
                  <Route path="/UPLOAD EXCEL/Chemical" element={<Chemical />} />
                  <Route path="/UPLOAD EXCEL/GoDown Domestic" element={<GoDownDomestic />} />
                  <Route path="/UPLOAD EXCEL/GoDown Export Import" element={<GoDownExportImport />} />
                  <Route path="/UPLOAD EXCEL/GoDown Stock" element={<GoDownStock />} />
                  <Route path="/UPLOAD EXCEL/Cane Office" element={<CaneOffice />} />

                  <Route path="/DATA APPROVAL/LAB DATA" element={<LabData />} />
                  <Route path="/DATA APPROVAL/Chemical" element={<Chemical />} />
                  <Route path="/DATA APPROVAL/GoDown Domestic" element={<GoDownDomestic />} />
                  <Route path="/DATA APPROVAL/GoDown Export Import" element={<GoDownExportImport />} />
                  <Route path="/DATA APPROVAL/GoDown Stock" element={<GoDownStock />} />
                  <Route path="/DATA APPROVAL/Cane Office" element={<CaneOffice />} />

                  <Route path="/DATA FEEDING/CANE DUES DATA" element={<CaneDuesData />} />
                  <Route path="/DATA APPROVAL/CANE DUES DATA" element={<CaneDuesData />} />

                  <Route path="/NSWS JSON/CREATE JSON" element={<ShowJon />} />
                  <Route path="/REPORT/MONTHLY DATA FEEDING" element={<PdfReport />} />
                  <Route path="/SEASON DATA/SEASON DATA" element={<SeasonData />} />
                  <Route path="/ACCESS USER RIGHT/USER CONTROL" element={<UserControl />} />
                  <Route path="/ACCESS USER RIGHT/Menu Right" element={<MenuRight />} />
                  <Route path="/ACCESS USER RIGHT/PASSWORD CHANGE" element={<PasswordChange />} />
                  <Route path="/Mill Details/Mill Details" element={<MillDetails />} />

                  <Route path="*" element={<PageNotFound />} />
                </Routes>
              </Suspense>
            </main>
          ) : (
            <Suspense fallback={pageLoading}>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/sisl" element={<CreateClient />} />
                <Route path="*" element={<PageNotFound />} />
              </Routes>
            </Suspense>
          )}
        </UserProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
}

export default App;
